import React, { useEffect, useState } from 'react';
import api from '../api';
import MarkdownEditor from './MarkdownEditor';
import MarkdownRenderer from './MarkdownRenderer';

export interface PortalIssueComment {
  id: number;
  issue_id: number;
  user_id: number;
  content: string;
  created_at: string;
  updated_at: string;
  user_name: string | null;
  user_email: string | null;
}

interface IssueCommentsProps {
  issueId: number;
  onCommentAdded?: () => void;
}

const IssueComments: React.FC<IssueCommentsProps> = ({ issueId, onCommentAdded }) => {
  const [comments, setComments] = useState<PortalIssueComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchComments = async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const res = await api.get<PortalIssueComment[]>(`/portal-issues/${issueId}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [issueId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = commentContent.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await api.post(`/portal-issues/${issueId}/comments`, { content: trimmed });
      setCommentContent('');
      await fetchComments();
      onCommentAdded?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (s: string) => {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Comments</h4>
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading comments…</p>
      ) : (
        <ul className="space-y-3">
          {comments.length === 0 ? (
            <li className="text-sm text-gray-500 dark:text-gray-400">No comments yet.</li>
          ) : (
            comments.map((c) => (
              <li key={c.id} className="pl-3 border-l-2 border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {c.user_name || c.user_email || 'Unknown'} · {formatDate(c.created_at)}
                </p>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <MarkdownRenderer content={c.content} />
                </div>
              </li>
            ))
          )}
        </ul>
      )}
      <form onSubmit={handleSubmit} className="space-y-2 w-full min-w-0">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Add a comment</label>
        <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-700 w-full min-w-0">
          <MarkdownEditor
            value={commentContent}
            onChange={setCommentContent}
            placeholder="Write a comment… (bold, lists, code, images supported)"
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !commentContent.trim()}
          className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
        >
          {submitting ? 'Posting…' : 'Post comment'}
        </button>
      </form>
    </div>
  );
};

export default IssueComments;
